const loadCSS = (href) => new Promise((resolve) => {
  try {
    if (document.querySelector(`link[href="${href}"]`)) return resolve(true);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.onload = () => resolve(true);
    link.onerror = () => resolve(false);
    document.head.appendChild(link);
  } catch { resolve(false); }
});

const loadTemplate = async (url) => {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return await res.text();
  } catch { return null; }
};

const modalElementFromHtml = (html) => {
  const host = document.createElement('div');
  host.innerHTML = html;
  return [...host.children].find((node) => node.tagName !== 'SCRIPT' && node.tagName !== 'STYLE') ?? null;
};

import loginModalController from './LoginModalController.js';
import { validation } from '../create/utils/validation.js';

export default class FeedbackModal {
  constructor() {
    this.modal = null;
    this.language = 'en';
    this.translations = null;
    this._boundClose = this._close.bind(this);
    this._boundLangChange = this._handleLanguageChange.bind(this);
    this._boundEscKey = this._handleEscKey.bind(this);
  }

  async show() {
    const { getCurrentLanguage } = await import('../translations.js');
    this.language = getCurrentLanguage();
    await loadCSS('/css/modals/feedback.css');
    const html = await loadTemplate('/components/modals/feedback-modal.html');
    if (!html) return;

    // Ensure single instance
    document.getElementById('feedback-modal')?.remove();
    this.modal = modalElementFromHtml(html);
    if (!this.modal) return;
    document.body.appendChild(this.modal);

    await this._loadTranslations();
    this._applyTranslations();
    this._bindEvents();

    requestAnimationFrame(() => {
      this.modal.style.display = 'flex';
      this.modal.classList.add('show');
    });
  }

  async _loadTranslations() {
    try {
      const res = await fetch(`/translations/feedback-modal-${this.language}.json`);
      if (res.ok) {
        this.translations = await res.json();
        return;
      }
    } catch {}
    try {
      const res = await fetch('/translations/feedback-modal-en.json');
      this.translations = await res.json();
    } catch { this.translations = {}; }
  }

  _t(key) {
    try {
      return key.split('.').reduce((o, k) => (o ? o[k] : undefined), this.translations) || '';
    } catch { return ''; }
  }

  _applyTranslations() {
    const m = this.modal;
    if (!m) return;
    const title = m.querySelector('[data-translate="feedback.title"]');
    const subtitle = m.querySelector('[data-translate="feedback.subtitle"]');
    const ph = m.querySelector('[data-translate="feedback.placeholder"]');
    const send = m.querySelector('[data-translate="feedback.send"]');
    const label = m.querySelector('[data-translate="feedback.label"]');

    if (title) title.textContent = this._t('title') || title.textContent;
    if (subtitle) subtitle.textContent = this._t('subtitle') || subtitle.textContent;
    if (ph) ph.placeholder = this._t('placeholder') || ph.placeholder;
    if (send) send.textContent = this._t('send') || send.textContent;
    if (label) label.textContent = this._t('label') || label.textContent;
  }

  async _handleLanguageChange(e) {
    if (e.detail?.language && e.detail.language !== this.language) {
      this.language = e.detail.language;
      await this._loadTranslations();
      this._applyTranslations();
    }
  }

  _handleEscKey(e) {
    if (e.key === 'Escape') {
      this._boundClose();
    }
  }

  _close() {
    if (!this.modal) return;
    this.modal.classList.remove('show');
    document.removeEventListener('languageChanged', this._boundLangChange);
    document.removeEventListener('keydown', this._boundEscKey);
    setTimeout(() => this.modal?.remove(), 150);
  }

  _bindEvents() {
    const m = this.modal;
    if (!m) return;
    m.addEventListener('click', (e) => { if (e.target === m) this._boundClose(); });
    m.querySelector('.close-btn')?.addEventListener('click', this._boundClose);
    document.addEventListener('languageChanged', this._boundLangChange);
    document.addEventListener('keydown', this._boundEscKey);

    const form = m.querySelector('#feedback-form');
    const textarea = m.querySelector('#feedback-message');
    const status = m.querySelector('#feedback-status');
    if (!form || !textarea || !status) return;

    validation.setupCharacterCount('feedback-message', 'feedback-message-counter', 500);

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const msg = (textarea.value || '').trim();
      if (!msg) {
        status.textContent = this._t('errors.empty') || 'Please write a message.';
        status.className = 'status error';
        return;
      }
      if (msg.length > 500) {
        status.textContent = this._t('errors.too_long') || 'Message too long (max 500).';
        status.className = 'status error';
        return;
      }
      status.className = 'status';
      status.textContent = this._t('status.sending') || 'Sending...';
      try {
        const { SERVER_URL } = await import('../config.js');
        const token = await window.tokenManager?.getValidAccessToken?.() ?? null;
        const res = await fetch(`${SERVER_URL}/api/v1/send-feedback-msg`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ message: msg })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.error) throw new Error(data.error || 'Failed');
        status.className = 'status';
        status.textContent = this._t('status.sent') || 'Thanks! We received your message.';
        textarea.value = '';
      } catch (err) {
        status.className = 'status error';
        status.textContent = this._t('status.error') || 'Could not send message.';
        console.error('Feedback send failed:', err);
      }
    });
  }
}


